export interface ReservationValidationSettings {
	maxReservationSize: number;
	maximumReservationsPerTimeSlot: number;
	reservationSlotDurationMinutes: number;
}

export interface ExistingReservationEvent {
	id?: number;
	typeId?: number;
	startDate?: string;
	startTime?: string;
	start?: string;
	numberOfAttendees?: number;
	size?: number;
}

export const parseReservationSlotDurationMinutes = (duration?: string): number => {
	if (!duration) return 30;
	const [hoursStr, minutesStr] = duration.split(':');
	const hours = Number(hoursStr);
	const minutes = Number(minutesStr ?? '0');
	if (Number.isNaN(hours) || Number.isNaN(minutes)) return 30;
	const totalMinutes = hours * 60 + minutes;
	return totalMinutes > 0 ? totalMinutes : 30;
};

export const extractDatePart = (dateInput: string): string | null => {
	const match = String(dateInput).match(/^(\d{4}-\d{2}-\d{2})/);
	return match ? match[1] : null;
};

export const normalizeTimeToHHmm = (timeInput: string): string => {
	const [hours = '0', minutes = '0'] = timeInput.split(':');
	return `${String(Number(hours)).padStart(2, '0')}:${String(Number(minutes)).padStart(2, '0')}`;
};

export const extractTimeFromDateInput = (dateInput: string): string => {
	const timeMatch = String(dateInput).match(/T(\d{2}):(\d{2})/);
	if (timeMatch) {
		return `${timeMatch[1]}:${timeMatch[2]}:00`;
	}
	return '00:00:00';
};

export const extractTypeIdFromIri = (iri: unknown): number => {
	if (!iri) return 0;
	if (typeof iri === 'number') return Number.isFinite(iri) ? iri : 0;
	if (typeof iri === 'string') {
		const id = Number.parseInt(iri.split('/').pop() || '0', 10);
		return Number.isNaN(id) ? 0 : id;
	}
	if (typeof iri === 'object' && iri !== null) {
		const record = iri as { id?: unknown; '@id'?: unknown };
		if (record.id !== undefined && record.id !== null) return extractTypeIdFromIri(record.id);
		if (record['@id']) return extractTypeIdFromIri(record['@id']);
	}
	return 0;
};

export const getReservationSlotKey = (
	dateInput: string,
	timeInput: string,
	slotDurationMinutes: number
): string | null => {
	const datePart = extractDatePart(dateInput);
	if (!datePart || slotDurationMinutes <= 0) return null;

	const normalizedTime = normalizeTimeToHHmm(timeInput);
	const [hours, minutes] = normalizedTime.split(':').map(Number);
	const totalMinutes = hours * 60 + minutes;
	const slotStartMinutes = Math.floor(totalMinutes / slotDurationMinutes) * slotDurationMinutes;
	const slotHour = Math.floor(slotStartMinutes / 60);
	const slotMinute = slotStartMinutes % 60;
	const slotTime = `${String(slotHour).padStart(2, '0')}:${String(slotMinute).padStart(2, '0')}`;
	return `${datePart}T${slotTime}`;
};

export const getEventAttendeeCount = (event: ExistingReservationEvent): number => {
	const count = Number(event.numberOfAttendees ?? event.size ?? 0);
	return Number.isFinite(count) && count > 0 ? count : 0;
};

export const getEventReservationSlotKey = (
	event: ExistingReservationEvent,
	slotDurationMinutes: number
): string | null => {
	const dateInput = event.startDate || event.start || '';
	const timeInput = event.startTime || extractTimeFromDateInput(dateInput);
	return getReservationSlotKey(dateInput, timeInput, slotDurationMinutes);
};

export const mapApiEventToReservationEvent = (event: any): ExistingReservationEvent => ({
	id: event?.id,
	typeId: extractTypeIdFromIri(event?.type),
	startDate: event?.startDate || event?.start,
	startTime: event?.startTime,
	start: event?.start,
	numberOfAttendees: event?.numberOfAttendees,
	size: event?.size,
});

export const getTotalAttendeesInSlot = (
	events: ExistingReservationEvent[],
	slotKey: string,
	slotDurationMinutes: number,
	reservationTypeIds: Set<number>,
	excludeEventId?: number | null
): number =>
	events.reduce((sum, event) => {
		if (excludeEventId && event.id === excludeEventId) return sum;
		if (!event.typeId || !reservationTypeIds.has(event.typeId)) return sum;

		const eventSlotKey = getEventReservationSlotKey(event, slotDurationMinutes);
		if (!eventSlotKey || eventSlotKey !== slotKey) return sum;

		return sum + getEventAttendeeCount(event);
	}, 0);

export const getReservationAttendeeWarning = (
	attendees: number,
	settings: Pick<ReservationValidationSettings, 'maxReservationSize'>,
	isReservation: boolean
): string | null => {
	if (!isReservation || !Number.isFinite(attendees) || attendees <= settings.maxReservationSize) {
		return null;
	}
	return `Number of attendees exceeds the configured max reservation size of ${settings.maxReservationSize}.`;
};

export const getSlotCapacityWarning = (
	newAttendees: number,
	existingTotalInSlot: number,
	maximumReservationsPerTimeSlot: number
): string | null => {
	if (existingTotalInSlot + newAttendees <= maximumReservationsPerTimeSlot) {
		return null;
	}
	return `This workplace allows only ${maximumReservationsPerTimeSlot} reservations within the given slot.`;
};

export const buildReservationTypeIdSet = (eventTypes: Array<{ id?: number | string; isReservation?: boolean }>): Set<number> =>
	new Set(
		eventTypes
			.filter((type) => type.isReservation === true)
			.map((type) => Number(type.id))
			.filter((id) => Number.isFinite(id) && id > 0)
	);

export const isReservationEventType = (
	eventTypeId: number,
	eventTypes: Array<{ id?: number | string; isReservation?: boolean }>
): boolean => {
	const selectedType = eventTypes.find((type) => Number(type.id) === Number(eventTypeId));
	return selectedType?.isReservation === true;
};

export const collectReservationWarnings = (params: {
	attendees: number;
	isReservation: boolean;
	settings: ReservationValidationSettings;
	startDate: string;
	startTime: string;
	existingEvents?: ExistingReservationEvent[];
	eventTypes: Array<{ id?: number | string; isReservation?: boolean }>;
	excludeEventId?: number | null;
}): string[] => {
	if (!params.isReservation || !Number.isFinite(params.attendees) || params.attendees < 1) {
		return [];
	}

	const warnings: string[] = [];
	const attendeeWarning = getReservationAttendeeWarning(
		params.attendees,
		{ maxReservationSize: params.settings.maxReservationSize },
		true
	);
	if (attendeeWarning) {
		warnings.push(attendeeWarning);
	}

	const datePart = extractDatePart(params.startDate);
	const slotKey = datePart
		? getReservationSlotKey(
				params.startDate,
				params.startTime,
				params.settings.reservationSlotDurationMinutes
			)
		: null;

	if (slotKey && params.existingEvents) {
		const reservationTypeIds = buildReservationTypeIdSet(params.eventTypes);
		const existingTotal = getTotalAttendeesInSlot(
			params.existingEvents,
			slotKey,
			params.settings.reservationSlotDurationMinutes,
			reservationTypeIds,
			params.excludeEventId
		);
		const slotWarning = getSlotCapacityWarning(
			params.attendees,
			existingTotal,
			params.settings.maximumReservationsPerTimeSlot
		);
		if (slotWarning) {
			warnings.push(slotWarning);
		}
	}

	return warnings;
};

export const checkReservationWarnings = async (params: {
	workplaceId: number;
	startDate: string;
	startTime: string;
	attendees: number;
	eventTypeId: number;
	eventTypes: Array<{ id?: number | string; isReservation?: boolean }>;
	settings: ReservationValidationSettings;
	excludeEventId?: number | null;
	fetchEvents: (args: { workplaceId: number; startDate: string }) => Promise<{ member?: any[] }>;
}): Promise<string[]> => {
	const isReservation = isReservationEventType(params.eventTypeId, params.eventTypes);
	if (!isReservation) return [];

	const datePart = extractDatePart(params.startDate);
	if (!datePart) return [];

	const data = await params.fetchEvents({
		workplaceId: params.workplaceId,
		startDate: datePart,
	});
	const existingEvents = (data?.member || []).map(mapApiEventToReservationEvent);

	return collectReservationWarnings({
		attendees: params.attendees,
		isReservation,
		settings: params.settings,
		startDate: params.startDate,
		startTime: params.startTime,
		existingEvents,
		eventTypes: params.eventTypes,
		excludeEventId: params.excludeEventId,
	});
};
